import { LoaderComponent, useLoader } from '@/generic/loader';
import { inject } from '@/diagnostic/injections.ts';
import { DIAGNOSTICS } from '@/diagnostic/DiagnosticKeys.ts';
import type { DiagnosticSummary } from '@/diagnostic/domain/DiagnosticSummary.ts';
import {
  IpponContainer,
  IpponCard,
  IpponGrid,
  IpponGridSlot,
  IpponHSpace,
  IpponText,
  IpponTitle,
  IpponVSpace,
} from '@ippon-ui/react';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { DiagnosticHistoryLoadingComponent } from '@/diagnostic/primary/DiagnosticHistoryLoadingComponent.tsx';
import { DiagnosticHistoryErrorComponent } from '@/diagnostic/primary/DiagnosticHistoryErrorComponent.tsx';
import { HistorySummaryComponent } from '@/diagnostic/primary/HistorySummaryComponent.tsx';
import { useDiagnosticReportModal } from '@/diagnostic/primary/UseDiagnosticReportModal.tsx';
import type { DiagnosticHasFinished } from '@/diagnostic/domain/DiagnosticHasFinished.ts';

const DiagnosticHistoryResolvedComponent = ({ history }: { history: DiagnosticSummary[] }) => {
  const { t } = useTranslation('diagnostic');
  const { openReport, modal } = useDiagnosticReportModal();

  return (
    <IpponContainer>
      <IpponCard size="large" shadow="l3">
        <IpponVSpace gap={10}>
          <hgroup>
            <IpponHSpace gap={8} align="middle">
              <IpponTitle tag="h2" level={5} dataSelector="diagnostic.history.title">
                {t('diagnostic.history.title')}
              </IpponTitle>
              <IpponText tag="p" variant="body" dataSelector="diagnostic.history.title-count">
                ({history.length})
              </IpponText>
            </IpponHSpace>
          </hgroup>
          <IpponGrid container={[4, 'm-12']} gap={[16, 'm-12']}>
            {history.map((summary) => (
              <IpponGridSlot col={[4, 'm-6']} key={summary.id.value}>
                <HistorySummaryComponent summary={summary} onOpen={openReport} />
              </IpponGridSlot>
            ))}
          </IpponGrid>
        </IpponVSpace>
      </IpponCard>
      {modal}
    </IpponContainer>
  );
};

export const DiagnosticHistoryComponent = () => {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const reload = (_event: DiagnosticHasFinished) => setVersion((v) => v + 1);
    return inject(DIAGNOSTICS).onDiagnosticCompleted(reload);
  }, []);

  const historyLoader = useLoader(() => inject(DIAGNOSTICS).history(6), [version]);

  const onLoading = () => <DiagnosticHistoryLoadingComponent />;

  const onSuccess = (history: DiagnosticSummary[]) => (
    <DiagnosticHistoryResolvedComponent history={history} />
  );

  const onError = (error: Error) => <DiagnosticHistoryErrorComponent error={error} />;

  return (
    <LoaderComponent
      loader={historyLoader}
      onLoading={onLoading}
      onSuccess={onSuccess}
      onError={onError}
    />
  );
};
